import { FormControl, InputLabelProps } from '@mui/material';
import { TimePicker, TimeValidationError, TimeView } from '@mui/x-date-pickers';
import { dateTimePickerConvertTime, MprcDateTimePickerFormatType } from 'components/MprcDateTimePicker';
import { useMprcTheme } from 'components/MprcThemeProvider';
import { useReferredState } from 'hooks/useReferredState';
import moment, { Moment } from 'moment';
import React, { useEffect, useState } from 'react';

////////////////////////////////////////////////////////////////////////////

interface IProps {
    disableOpenPicker?: boolean;
    autoComplete?: string,
    autoFocus?: boolean;
    views?: readonly TimeView[];
    value: Date | null;
    format?: string;
    timePickerFormat?: MprcDateTimePickerFormatType;
    fullWidth?: boolean;
    width?: string | number;
    margin?: MuiMargin;
    label?: string;
    title?: string;
    placeholder?: string;
    disabled?: boolean;
    error?: boolean;
    helperText?: React.ReactNode;
    required?: boolean;
    InputLabelProps?: Partial<InputLabelProps>;
    startAdornment?: React.ReactElement;
    endAdornment?: React.ReactElement;
    onChange: (date: Date | null, valid: boolean) => void;
    onError?: (error: TimeValidationError, value: Date | null) => void;
    onBlur?: () => void;
}

////////////////////////////////////////////////////////////////////////////

export const MprcTimePicker: React.FC<IProps> = (props) => {
    const { theme } = useMprcTheme();

    const {
        InputLabelProps, views, autoComplete, fullWidth, width = "120px", timePickerFormat = "default", format,
        margin, autoFocus, required, value, placeholder, disabled, disableOpenPicker, error, helperText, label, title,
        startAdornment, endAdornment,
        onChange, onError, onBlur
    } = props;

    const [time, setTime, refTime] = useReferredState<Moment | null>(dateTimePickerConvertTime(value, timePickerFormat));
    const [valid, setValid] = useState<boolean>(true);

    ///
    const updateValue = (dtMoment: Moment | null) => {
        const isValid = dtMoment === null || dtMoment.isValid();
        onChange(dtMoment === null ? null : moment(refTime.current, "HH:mm:ss").toDate(), isValid);
    };

    ///
    const handleUpdateTime = (dtMoment: Moment | null) => {
        setTime(dtMoment);
        updateValue(dtMoment);
    };

    ///
    useEffect(() => {
        var mDate = value == null ? null : moment(value);
        if (mDate === null || mDate.isValid()) {
            setTime(dateTimePickerConvertTime(value, timePickerFormat));
        }
    }, [value]);

    ///
    useEffect(() => {
        // Para não alterar o valor diferente para campos é verificado se a hora é diferente.
        // isso porque ao formatar é modificado o valor de (value) deixando o valor diferente do JSON vindo do backend.
        const checkValue = value == null ? null : moment(value);
        const isEqual = (time == null && checkValue == null) || time?.isSame(checkValue);
        if (!isEqual) {
            onChange(time == null ? null : time.toDate(), valid);
        }
    }, [valid]);

    ///
    return (
        <FormControl margin={margin} fullWidth={fullWidth}>
            <TimePicker
                label={label}
                disabled={disabled}
                disableOpenPicker={disableOpenPicker}
                value={time}
                autoFocus={autoFocus}
                views={views}
                format={format}
                slotProps={{
                    textField: {
                        required: required,
                        placeholder: placeholder ?? (format == "initialDate"
                            ? theme.MprcDateTimePicker.firstTimeOfDayText
                            : format == "endDate"
                                ? theme.MprcDateTimePicker.endTimeOfDayText
                                : undefined),
                        title: title,
                        style: { width: fullWidth ? undefined : width },
                        onBlur: onBlur,
                        InputProps: {
                            autoComplete: autoComplete,
                            startAdornment: startAdornment,
                            endAdornment: endAdornment,
                        },
                        InputLabelProps: InputLabelProps,
                        error: valid === false || error,
                        helperText: helperText
                    }
                }}
                onChange={(newValue) => handleUpdateTime(newValue)}
                onError={(error, value) => {
                    setValid(error === null);
                    onError && onError(error, value?.toDate() ?? null);
                }}
            />
        </FormControl>
    );
};
